import React, { useState, useEffect, createContext, useContext } from 'react';
import { WifiOff, Wifi, RefreshCw, AlertCircle } from 'lucide-react';

// Offline Context
const OfflineContext = createContext({ isOffline: false, isOnline: true });

export const useOffline = () => useContext(OfflineContext);

/**
 * OfflineProvider - Provides offline status to the app
 */
export function OfflineProvider({ children }) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setShowBanner(true);
      // Hide "back online" banner after 3 seconds
      setTimeout(() => setShowBanner(false), 3000);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <OfflineContext.Provider value={{ isOffline, isOnline: !isOffline }}>
      {children}
      {showBanner && <OfflineBanner isOffline={isOffline} onDismiss={() => setShowBanner(false)} />}
    </OfflineContext.Provider>
  );
}

/**
 * OfflineBanner - Shows offline/online status
 */
export function OfflineBanner({ isOffline, onDismiss }) {
  return (
    <div
      className={`
        fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-center gap-2 text-white text-sm font-medium
        transition-all duration-300 transform
        ${isOffline ? 'bg-red-500' : 'bg-green-500'}
      `}
      onClick={onDismiss}
    >
      {isOffline ? (
        <>
          <WifiOff className="h-4 w-4" />
          <span>You are offline. Some features may be unavailable.</span>
        </>
      ) : (
        <>
          <Wifi className="h-4 w-4" />
          <span>Back online!</span>
        </>
      )}
    </div>
  );
}

/**
 * OfflineIndicator - Small indicator for headers/sidebars
 */
export function OfflineIndicator({ className = '' }) {
  const { isOffline } = useOffline();

  if (!isOffline) return null;

  return (
    <div className={`flex items-center gap-1 text-red-500 text-xs ${className}`}>
      <WifiOff className="h-3 w-3" />
      <span>Offline</span>
    </div>
  );
}

/**
 * OfflineFallback - Component to show when offline and no cached data
 */
export function OfflineFallback({ onRetry, message = 'Unable to load data while offline' }) {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry?.();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <AlertCircle className="h-12 w-12 text-slate-400 mb-4" />
      <h3 className="text-lg font-semibold text-slate-700 mb-2">You're Offline</h3>
      <p className="text-slate-500 mb-4">{message}</p>
      {onRetry && (
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
          {isRetrying ? 'Retrying...' : 'Retry'}
        </button>
      )}
    </div>
  );
}

/**
 * useOnlineStatus - Hook to track online status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

export default OfflineProvider;
